<template>
  <template v-if="surveyCfg.answerStatus !== 3">
    <div class="xy-survey" :style="getStyle('totalStyle')" ref="componentRef">
      <!-- 表单组件 -->
      <survey-form-container :model="surveyCfg.formData" :rules="surveyCfg.rules" ref="surveyRef">
        <div :style="getStyle('questionStyle')" v-for="(question, questionIndex) in surveyCfg.questions" :key="question.id">
          <survey-form-item
            :language="surveyCfg.language"
            :fieldName="question.id"
            :typeName="getTypeName(question.type)"
            :label="getLabel(questionIndex, question.title)"
            :style="getStyle('questionStyle')"
            :showValidateMsg="showValidateMsg"
            :showTag="showTag">
            <template #default="{ modelValue, updateValue }">
              <!-- 带选项的组件 -->
              <component
                v-if="![1, 2].includes(question.type)"
                :is="COMPONENT_MAP[question.type]"
                :modelValue="modelValue"
                @update:modelValue="updateValue"
                v-model:otherValue="surveyCfg.formDataWithOther[question.id]"
                v-bind="getAttrs(question)"
                :options="getOptions(question)"
                :disabled="surveyCfg.answerStatus === 2"
                :inputFormat="getInputFormat(question)"
                @change="handleFormItemChange"></component>
              <!-- 不带选项的组件 -->
              <template v-else>
                <component
                  v-if="getInputFormat(question) === 1"
                  :is="COMPONENT_MAP[question.type]"
                  v-model="surveyCfg.formData[question.id]"
                  @update:modelValue="updateValue"
                  v-bind="getAttrs(question)"
                  :disabled="surveyCfg.answerStatus === 2"
                  @change="handleFormItemChange"></component>
                <component
                  v-else
                  :is="COMPONENT_NUMBER_MAP[question.type]"
                  v-model="surveyCfg.formData[question.id]"
                  @update:modelValue="updateValue"
                  v-bind="getAttrs(question)"
                  :disabled="surveyCfg.answerStatus === 2"
                  @change="handleFormItemChange"></component>
              </template>
            </template>
          </survey-form-item>
        </div>
      </survey-form-container>
    </div>
  </template>
  <template v-else>
    <!-- 组件空白时的展示 -->
    <div class="flex-center">
      <img src="@/packages/assets/images/survey-empty.png" alt="empty survey" />
      <p :style="{ marginTop: computeRatioPx(16), fontSize: computeRatioPx(36) }">{{ LGSdk.t('survey.10703') }}</p>
    </div>
  </template>
</template>

<script setup>
import { ref } from 'vue'
import { info, defaultProps } from './config'
import { useSurvey } from '@/packages/components/hook/useSurvey'
import { computeRatioPx } from '@/packages/plugins/utils'
import { TYPE_TO_INIT_DATA, COMPONENT_MAP, COMPONENT_NUMBER_MAP, SURVEY_QUESTION_TYPE_ITEM } from './constant'
import surveyFormContainer from './components/surveyFormContainer.vue'
import surveyFormItem from './components/surveyFormItem.vue'
import LGSdk from '@/packages/plugins/languageSDK/index'

defineOptions({ name: info.name })
const props = defineProps(defaultProps)

// 问卷配置
const surveyCfg = ref({
  style: {
    width: 750,
    height: 750,
    background: '#fff',
    backgroundImage: ''
  },
  questionStyle: {
    fontSize: 16,
    marginTop: 10,
    marginBottom: 10,
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 20,
    paddingRight: 20
  },
  questions: [],
  formData: {},
  formDataWithOther: {},
  rules: {},
  answerStatus: 1,
  isFirstChange: true,
  language: 'en'
})

/* 获取样式 */
const getStyle = (type) => {
  if (type === 'totalStyle') {
    return surveyCfg.value.style
  } else if (type === 'questionStyle') {
    const questionStyle = { ...surveyCfg.value.questionStyle }
    if (!props.isCard) {
      questionStyle.paddingTop = undefined
      questionStyle.paddingBottom = undefined
      questionStyle.paddingLeft = undefined
      questionStyle.paddingRight = undefined
      questionStyle.backgroundColor = undefined
      questionStyle.borderRadius = undefined
    }
    return questionStyle
  }
}

/* 获取标签 */
const getLabel = (index, title) => {
  return props.showIndex ? index + 1 + '. ' + title : title
}

/* 获取组件attrs */
const getAttrs = (data) => {
  if ([1, 2].includes(data.type)) {
    /* 文本输入框 */
    return {
      ...data.options[0]?.attrs,
      ...data.attrs,
      style: {
        ...data.optionStyle
      }
    }
  } else if ([4, 6].includes(data.type)) {
    /* 多选框 */
    return {
      ...data.attrs,
      style: {
        ...data.optionStyle
      }
    }
  } else {
    return {
      style: {
        ...data.optionStyle
      }
    }
  }
}

/* 获取输入格式限制 */
const getInputFormat = (data) => {
  return data?.inputFormat || 1
}

/* 获取组件选项 */
const getOptions = (data) => {
  return data.options || []
}

/* 获取组件类型名称 */
const getTypeName = (type) => {
  let name = ''
  switch (type) {
    case 3:
      name = LGSdk.t('public.radio')
      break
    case 5:
      name = LGSdk.t('public.radio')
      break
    case 4:
      name = LGSdk.t('public.checkbox')
      break
    case 6:
      name = LGSdk.t('public.checkbox')
      break
  }
  return name
}

/* 表单子项change事件 */
const handleFormItemChange = () => {
  if (surveyCfg.value.isFirstChange) {
    surveyCfg.value.isFirstChange = false
    reportTime(Date.now())
  }
}

/* 处理formData的数据转换 */
const dealWithFormData = (questionList) => {
  const answerList = []
  questionList.forEach((question) => {
    const answer = {
      questionId: question.questionId,
      optionList: []
    }
    const optionData = surveyCfg.value.formData[question.questionId]
    const otherOptionData = surveyCfg.value.formDataWithOther[question.questionId]
    if (Array.isArray(optionData)) {
      // 多选的情况
      if (optionData?.length) {
        answer.optionList = optionData?.map((option) => {
          const isOther = question.optionList.find((o) => o.optionId === option)
          const res = {
            type: question.type,
            optionId: option,
            textAnswer: isOther?.otherOption ? otherOptionData : ''
          }
          return res
        })
      } else {
        answer.optionList = [
          {
            type: question.type,
            optionId: '',
            textAnswer: ''
          }
        ]
      }
    } else {
      const option = {
        type: question.type,
        optionId: '',
        textAnswer: ''
      }
      // 单选的情况
      if ([3, 5].includes(question.type)) {
        const isOther = question.optionList.find((option) => option.optionId === optionData)
        option.optionId = optionData
        option.textAnswer = isOther?.otherOption ? otherOptionData : ''
      }
      // 文本输入的情况
      if ([1, 2].includes(question.type)) {
        option.textAnswer = optionData
        option.optionId = question.optionList[0].optionId
      }
      answer.optionList = [option]
    }
    answerList.push(answer)
  })
  return answerList
}

/* 设置填充自动识别内容 */
const setAutoRecognition = (dataMap) => {
  for (const [key, value] of dataMap.entries()) {
    surveyCfg.value.formData[key] = value
  }
  if (dataMap.size) {
    handleFormItemChange()
  }
}

/* 设置问题和初始化表单 */
const setQuestions = (surveyData, language) => {
  const questions = []
  const isBackfill = surveyCfg.value.answerStatus === 2
  if (surveyData.questionList?.length) {
    surveyData.questionList.forEach((question) => {
      const questionItem = props.questions.find((q) => q.id === question.questionId)
      const questionTypeItem = props.questionTypeList?.find((q) => q.type === question.type)
      const addQuestionItem = {
        id: question.questionId,
        title: question.questionTitle,
        type: question.type,
        inputFormat: question.inputFormat,
        options: [],
        attrs: {
          wordLimit: question.wordLimit,
          minSelectable: question.minSelectable,
          maxSelectable: question.maxSelectable
        }
      }
      if (questionItem) {
        const fontSize = questionItem.optionStyle.optionFontSize || questionTypeItem?.optionStyle?.optionFontSize || 0
        const marginTop = questionItem.optionStyle.optionMarginTop || questionTypeItem?.optionStyle?.optionMarginTop || 0
        const marginBottom = questionItem.optionStyle.optionMarginBottom || questionTypeItem?.optionStyle?.optionMarginBottom || 0
        const paddingTop = questionItem.optionStyle.optionPaddingTop || questionTypeItem?.optionStyle?.optionPaddingTop || 0
        const paddingBottom = questionItem.optionStyle.optionPaddingBottom || questionTypeItem?.optionStyle?.optionPaddingBottom || 0
        const paddingLeft = questionItem.optionStyle.optionPaddingLeft || questionTypeItem?.optionStyle?.optionPaddingLeft || 0
        const paddingRight = questionItem.optionStyle.optionPaddingRight || questionTypeItem?.optionStyle?.optionPaddingRight || 0
        const borderRadius = questionItem.optionStyle.optionRadius || questionTypeItem?.optionStyle?.optionRadius || 0
        const imgHeight = questionItem.optionStyle.optionImgHeight || questionTypeItem?.optionStyle?.optionImgHeight || 0
        const imgWidth = questionItem.optionStyle.optionImgWidth || questionTypeItem?.optionStyle?.optionImgWidth || 0
        const imgRadius = questionItem.optionStyle.optionImgRadius || questionTypeItem?.optionStyle?.optionImgRadius || 0
        const width = questionItem.optionStyle.optionWidth || questionTypeItem?.optionStyle?.optionWidth || 0
        const background = questionItem.optionStyle.optionBackgroundColor || questionTypeItem?.optionStyle?.optionBackgroundColor || ''
        const optionBorderSize = questionItem.optionStyle.optionBorderSize || questionTypeItem?.optionStyle?.optionBorderSize || 0
        const optionOnlyBorderBottom = questionItem.optionStyle.optionOnlyBorderBottom || questionTypeItem?.optionStyle?.optionOnlyBorderBottom || false
        const optionBorderColor = questionItem.optionStyle.optionBorderColor || questionTypeItem?.optionStyle?.optionBorderColor || ''
        const optionOtherWidth = questionItem.optionStyle.optionOtherWidth || questionTypeItem?.optionStyle?.optionOtherWidth || 0
        const optionOtherBackgroundColor = questionItem.optionStyle.optionOtherBackgroundColor || questionTypeItem?.optionStyle?.optionOtherBackgroundColor || ''
        const optionOtherBorderSize = questionItem.optionStyle.optionOtherBorderSize || questionTypeItem?.optionStyle?.optionOtherBorderSize || 0
        const optionOtherBorderColor = questionItem.optionStyle.optionOtherBorderColor || questionTypeItem?.optionStyle?.optionOtherBorderColor || ''
        const optionOtherBorderRadius = questionItem.optionStyle.optionOtherBorderRadius || questionTypeItem?.optionStyle?.optionOtherBorderRadius || 0
        const textAreaRows = questionItem.optionStyle.textAreaRows || questionTypeItem?.optionStyle?.textAreaRows || 3
        const selectBg = questionItem.optionStyle.selectBg || questionTypeItem?.optionStyle?.selectBg || SURVEY_QUESTION_TYPE_ITEM.selectBg
        const selectedBg = questionItem.optionStyle.selectedBg || questionTypeItem?.optionStyle?.selectedBg || SURVEY_QUESTION_TYPE_ITEM.selectedBg
        const selectMark = questionItem.optionStyle.selectMark || questionTypeItem?.optionStyle?.selectMark || SURVEY_QUESTION_TYPE_ITEM.selectMark
        const selectBorderColor = questionItem.optionStyle.selectBorderColor || questionTypeItem?.optionStyle?.selectBorderColor || SURVEY_QUESTION_TYPE_ITEM.selectBorderColor
        addQuestionItem.optionStyle = {
          fontSize: computeRatioPx(fontSize),
          marginTop: computeRatioPx(marginTop),
          marginBottom: computeRatioPx(marginBottom),
          paddingTop: computeRatioPx(paddingTop),
          paddingBottom: computeRatioPx(paddingBottom),
          paddingLeft: computeRatioPx(paddingLeft),
          paddingRight: computeRatioPx(paddingRight),
          borderRadius: computeRatioPx(borderRadius),
          imgHeight: computeRatioPx(imgHeight),
          imgWidth: computeRatioPx(imgWidth),
          imgRadius: computeRatioPx(imgRadius),
          width: computeRatioPx(width),
          textAreaRows,
          background,
          selectBg,
          selectedBg,
          selectMark,
          selectBorderColor
        }
        if (optionBorderSize) {
          if (optionOnlyBorderBottom) {
            addQuestionItem.optionStyle.border = 0
            addQuestionItem.optionStyle.borderRadius = 0
            addQuestionItem.optionStyle.borderBottom = `${computeRatioPx(optionBorderSize)} solid ${optionBorderColor || ''}`
          } else {
            addQuestionItem.optionStyle.border = `${computeRatioPx(optionBorderSize)} solid ${optionBorderColor || ''}`
          }
        }
        if ([5, 6].includes(addQuestionItem.type)) {
          addQuestionItem.optionStyle.otherWidth = optionOtherWidth ? computeRatioPx(optionOtherWidth) : '100%'
          addQuestionItem.optionStyle.otherBackground = optionOtherBackgroundColor
          // 其他输入框样式
          addQuestionItem.optionStyle.otherBorderSize = optionOtherBorderSize ? computeRatioPx(optionOtherBorderSize) : 0
          addQuestionItem.optionStyle.otherBorderRadius = computeRatioPx(optionOtherBorderRadius || 0)
          addQuestionItem.optionStyle.otherBorder = `${computeRatioPx(optionOtherBorderSize || 0)} solid ${optionOtherBorderColor || ''}`
          addQuestionItem.optionStyle.onlyBorderBottom = optionOnlyBorderBottom
        }
        if (questionItem?.options?.length) {
          questionItem.options.forEach((option) => {
            const curOption = question.optionList.find((o) => o.optionId === option.id)
            const languageItem = option.languages.find((item) => item.language === language)
            addQuestionItem.options.push({
              label: curOption.optionTitle,
              value: curOption.optionId,
              selected: curOption.selected,
              otherOption: curOption.otherOption,
              optionUrl: curOption.optionUrl,
              textAnswer: curOption.textAnswer,
              attrs: {
                placeholder: languageItem?.placeholder || ''
              }
            })
          })
        }
        if (isBackfill) {
          // 文本输入的情况
          if ([1, 2].includes(question.type)) {
            surveyCfg.value.formData[question.questionId] = question.optionList[0].textAnswer
          }
          // 单选的情况
          if ([3, 5].includes(question.type)) {
            const selectedItem = question.optionList.find((option) => option.selected)
            surveyCfg.value.formData[question.questionId] = selectedItem?.optionId || ''
            if (selectedItem?.otherOption) {
              surveyCfg.value.formDataWithOther[question.questionId] = selectedItem.textAnswer
            }
          }
          // 多选的情况
          if ([4, 6].includes(question.type)) {
            const selectedArr = question.optionList.filter((option) => option.selected)
            const otherItem = question.optionList.find((option) => option.otherOption)
            surveyCfg.value.formData[question.questionId] = selectedArr.map((item) => {
              return item.optionId
            })
            if (otherItem?.otherOption) {
              surveyCfg.value.formDataWithOther[question.questionId] = otherItem.textAnswer
            }
          }
        }
      }
      questions.push(addQuestionItem)
    })
  }
  surveyCfg.value.questions = questions
  surveyCfg.value.language = language
  console.log('qqq', questions, surveyCfg.value.language)
}

/* 初始化样式配置和表单 */
const initSurvey = (initSurveyData) => {
  surveyCfg.value.style = {
    background: props.backgroundColor,
    backgroundImage: props.backgroundSrc ? `url('${props.backgroundSrc}')` : '',
    backgroundSize: 'cover',
    borderRadius: computeRatioPx(props.radius)
  }
  surveyCfg.value.questionStyle = {
    color: props.fontColor,
    tagColor: props.tagFontColor,
    tagBackground: props.tagBackgroundColor,
    fontSize: computeRatioPx(props.titleFontSize),
    marginTop: computeRatioPx(props.titleMarginTop),
    marginBottom: computeRatioPx(props.titleMarginBottom),
    marginLeft: computeRatioPx(props.titleMarginLeft),
    marginRight: computeRatioPx(props.titleMarginRight),
    paddingTop: computeRatioPx(props.cardPaddingTop),
    paddingBottom: computeRatioPx(props.cardPaddingBottom),
    paddingLeft: computeRatioPx(props.cardPaddingLeft),
    paddingRight: computeRatioPx(props.cardPaddingRight),
    background: props.cardBackgroundColor,
    borderRadius: computeRatioPx(props.cardBorderRadius)
  }
  if (initSurveyData.questionList?.length) {
    initSurveyData.questionList.forEach((question) => {
      surveyCfg.value.formData[question.questionId] = ![1, 2].includes(question.type) || question.inputFormat === 1 ? TYPE_TO_INIT_DATA[question.type] : null
      if ([5, 6].includes(question.type)) {
        surveyCfg.value.formDataWithOther[question.questionId] = question.inputFormat === 1 ? '' : null
      }
      let ruleObj = {
        required: false
      }
      if (question.required) {
        ruleObj.required = true
      }
      if ([1, 2].includes(question.type)) {
        ruleObj.validator = (value) => {
          return new Promise((resolve, reject) => {
            if (question.wordLimit && value?.length > question.wordLimit) {
              reject({ message: LGSdk.t('survey.maxInput') })
            } else if (value && question.inputFormat === 2 && !validatorNumber(value)) {
              reject({ message: LGSdk.t('survey.isNumber') })
            }
            resolve()
          })
        }
      } else if ([4, 6].includes(question.type)) {
        // 多选情况下，需要额外判断是否选少了
        ruleObj.validator = (value) => {
          return new Promise((resolve, reject) => {
            if (question.required && value?.length < question.minSelectable) {
              reject({ message: LGSdk.t('survey.minSelectable'), isShowValidate: true })
            } else if (question.required && value?.length > question.maxSelectable) {
              reject({ message: LGSdk.t('survey.maxSelectable') })
            } else if (question.inputFormat === 2) {
              /* 查找问题对应的other值，然后判断 */
              const otherValue = surveyCfg.value.formDataWithOther[question.questionId]
              if (otherValue && !validatorNumber(otherValue)) {
                reject({ message: LGSdk.t('survey.isNumber') })
              }
            }
            resolve()
          })
        }
      } else if ([5].includes(question.type) && question.inputFormat === 2) {
        ruleObj.validator = () => {
          return new Promise((resolve, reject) => {
            const otherValue = surveyCfg.value.formDataWithOther[question.questionId]
            if (otherValue && !validatorNumber(otherValue)) {
              reject({ message: LGSdk.t('survey.isNumber') })
            }
            resolve()
          })
        }
      }
      surveyCfg.value.rules[question.questionId] = ruleObj
    })
  }
  setAnswerStatus(initSurveyData.answerStatus)
}

const validatorNumber = (value) => {
  // 处理空值情况
  if (value === null || value === undefined || value === '') {
    return false
  }

  // 处理字符串情况
  if (typeof value === 'string') {
    // 允许科学计数法
    return /^-?\d+(\.\d+)?(e[+-]?\d+)?$/.test(value.trim())
  }

  // 处理数字情况
  if (typeof value === 'number') {
    return !isNaN(value) && isFinite(value)
  }

  return false
}

const setAnswerStatus = (status) => {
  surveyCfg.value.answerStatus = Number(status)
}

const { componentRef, surveyRef, getConfig, reportTime, submitForm } = useSurvey(props, initSurvey, setQuestions, setAutoRecognition, dealWithFormData, setAnswerStatus)

defineExpose({
  getConfig,
  submitForm
})
</script>

<style lang="scss" scoped>
.xy-survey {
  min-height: fit-content;
  :deep(input) {
    box-sizing: border-box;
  }
}
.flex-center {
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  img {
    max-width: 100%;
    max-height: 100%;
  }
}

* {
  box-sizing: border-box;
}
</style>
